import React from 'react';
import { useDashboard } from '@/context/DashboardContext';
import { GRANT_FTE, BAND_ALLOC, OVER_ALLOC, UNDER_ALLOC, ORG } from '@/data/pipeline';
import { cr, crFull, daysFrom, fmtDate } from '@/lib/fmt';
import KpiCard from '@/components/KpiCard';
import { Chip, ProgramChip } from '@/components/UiBits';
import { Users, TrendingDown, Wallet, AlertTriangle, UserMinus, Clock } from 'lucide-react';

// Colour cell by cell value (People-count intensity — sequential Blues)
const cellColor = (v, max) => {
  const t = v / max;
  return `rgba(43, 108, 176, ${0.1 + t * 0.75})`;
};

export default function Resources() {
  const { donors, grants, PROGRAMS: P, TODAY } = useDashboard();

  const totalFte = Object.values(GRANT_FTE).reduce((s, [f]) => s + f, 0);
  const avgPeopleShare = (() => {
    const vals = Object.values(GRANT_FTE).map(([, p]) => p);
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  })();

  const donorById = (id) => donors.find((d) => d.id === id);

  // People-side renewal risk: grants ending within 2Q
  const ending = grants
    .filter((g) => daysFrom(g.end, TODAY) <= 180 && GRANT_FTE[g.id])
    .map((g) => {
      const [fte, pct] = GRANT_FTE[g.id];
      const monthlySalary = fte * ORG.avgMonthlySalaryL; // ₹ Lakh
      return { g, fte, pct, monthlySalary };
    })
    .sort((a, b) => new Date(a.g.end) - new Date(b.g.end));

  const bandMax = Math.max(...BAND_ALLOC.cells.flat());

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">People &amp; salaries</div>
          <h1>Resource Utilisation</h1>
          <div className="sub">Staff allocated across grants, salary recovery, and audit exposure on split allocations.</div>
        </div>
      </div>

      <div className="kpi-row">
        <KpiCard icon={Users}         iconCls="ic-gold"   value={totalFte}                     label="FTEs Charged to Grants" delta={['up','of ' + ORG.headcount + ' staff']} />
        <KpiCard icon={Wallet}        iconCls="ic-green"  value={ORG.salaryRecovery + '%'}     label="Salary Recovery"        delta={['up','vs 71% last FY']} />
        <KpiCard icon={AlertTriangle} iconCls="ic-red"    value={OVER_ALLOC.length}            label="Over-Allocated (>100%)" delta={['down','audit exposure']} />
        <KpiCard icon={UserMinus}     iconCls="ic-amber"  value={UNDER_ALLOC.length}           label="Under-Allocated (<80%)" delta={['flat','burning core funds']} />
        <KpiCard icon={Clock}         iconCls="ic-blue"   value={cr(ending.reduce((s, r) => s + r.monthlySalary / 100, 0) * 6)} label="Salary on grants ending 2Q" delta={['down','redeployment needed']} />
      </div>

      <div className="grid-32">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">People-cost share of grant budget</div>
              <div className="card-sub">FTEs charged per grant · people-cost % of committed</div>
            </div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Grant</th><th>Program</th><th className="num">FTEs</th><th className="num">People-cost %</th><th className="num">Sanct.</th></tr>
              </thead>
              <tbody>
                {grants.filter((g) => GRANT_FTE[g.id]).map((g) => {
                  const [fte, pct] = GRANT_FTE[g.id];
                  const flag = pct >= 55 ? 'atrisk' : pct >= 45 ? 'pending' : 'ontrack';
                  return (
                    <tr key={g.id}>
                      <td><span className="ent">{g.name}</span><span className="sub">{donorById(g.donor)?.name}</span></td>
                      <td><ProgramChip program={g.program} PROGRAMS={P} /></td>
                      <td className="num">{fte}</td>
                      <td className="num"><Chip kind={flag === 'atrisk' ? 'At Risk' : flag === 'pending' ? 'Watch' : 'On Track'}>{pct}%</Chip></td>
                      <td className="num" title={crFull(g.sanct)}>{cr(g.sanct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[12.5px] text-[color:var(--gray-500)]">
            Avg. people-cost share across the portfolio: <b style={{ color: 'var(--gray-800)' }}>{avgPeopleShare}%</b> of grant budgets · flagged when &gt; 55%.
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Role Band × Grant Heatmap</div>
              <div className="card-sub">FTEs charged (aggregate — no names)</div>
            </div>
          </div>
          <div className="heat-wrap">
            <table className="heat">
              <thead>
                <tr>
                  <th className="rowh"></th>
                  {BAND_ALLOC.grants.map((gid) => (
                    <th key={gid} title={grants.find((x) => x.id === gid)?.name || gid}>{gid}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BAND_ALLOC.bands.map((band, r) => (
                  <tr key={band}>
                    <td className="rowl">{band}</td>
                    {BAND_ALLOC.cells[r].map((v, c) => (
                      <td key={c} style={{ background: cellColor(v, bandMax), color: '#1F2937' }} title={`${band} · ${BAND_ALLOC.grants[c]} · ${v} FTEs`}>
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Over-allocated Staff (&gt; 100%)</div>
              <div className="card-sub">Audit exposure — cannot be double-charged to donors</div>
            </div>
            <span className="chip atrisk">{OVER_ALLOC.length} people</span>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Person · Role</th><th>Allocations</th><th className="num">Total</th></tr>
              </thead>
              <tbody>
                {OVER_ALLOC.map((p, i) => {
                  const total = p.alloc.reduce((s, [, v]) => s + v, 0);
                  return (
                    <tr key={i}>
                      <td><span className="ent">{p.name}</span><span className="sub">{p.role}</span></td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {p.alloc.map(([gid, v], j) => (
                            <span key={j} className="chip neutral" title={grants.find((x) => x.id === gid)?.name || gid}>
                              {gid} · {v}%
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="num" style={{ color: 'var(--red-600)', fontWeight: 700 }}>{total}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Under-allocated Staff (&lt; 80%)</div>
              <div className="card-sub">Salary burning core funds — reallocation candidates</div>
            </div>
            <span className="chip pending">{UNDER_ALLOC.length} people</span>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Person · Role</th><th>Allocation</th><th className="num">Charged</th></tr>
              </thead>
              <tbody>
                {UNDER_ALLOC.map((p, i) => {
                  const total = p.alloc.reduce((s, [, v]) => s + v, 0);
                  return (
                    <tr key={i}>
                      <td><span className="ent">{p.name}</span><span className="sub">{p.role}</span></td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {p.alloc.map(([gid, v], j) => (
                            <span key={j} className="chip neutral">{gid} · {v}%</span>
                          ))}
                        </div>
                      </td>
                      <td className="num" style={{ color: 'var(--amber-600)', fontWeight: 700 }}>{total}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">People-side renewal risk</div>
            <div className="card-sub">FTEs + monthly salary sitting on grants ending within 2 quarters</div>
          </div>
          <Chip kind="Watch">Redeployment plan required</Chip>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Donor · Grant</th><th>Program</th><th>Ends</th><th className="num">FTEs</th><th className="num">Monthly ₹</th><th className="num">6-mo exposure</th></tr>
            </thead>
            <tbody>
              {ending.map(({ g, fte, monthlySalary }) => (
                <tr key={g.id}>
                  <td><span className="ent">{donorById(g.donor)?.name}</span><span className="sub">{g.name}</span></td>
                  <td><ProgramChip program={g.program} PROGRAMS={P} /></td>
                  <td>{fmtDate(g.end)}</td>
                  <td className="num">{fte}</td>
                  <td className="num">₹{monthlySalary.toFixed(1)} L</td>
                  <td className="num" style={{ color: 'var(--red-600)', fontWeight: 700 }}>
                    {cr(monthlySalary * 6 / 100)}
                  </td>
                </tr>
              ))}
              {ending.length === 0 && (
                <tr><td colSpan={6}><div className="empty-state"><div className="empty-title">No people-side risk in the next 2 quarters</div></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
